import {
  ZapierExport,
  ZapierWorkflow,
  ZapierNode,
  ParsedWorkflow,
  FlowNode,
  FlowEdge,
} from '../types/zapier';
import { calculateLayout, enhanceEdgesForBranching, hasBranching } from './graphLayout';
import {
  SchemaError,
  EmptyWorkflowError,
  NoWorkflowsFoundError,
  UnsupportedSchemaVersionError,
  InvalidNodeStructureError,
  CyclicDependencyError,
  logError,
} from './errors';

/**
 * Determines the node type based on Zapier node properties
 */
function determineNodeType(node: ZapierNode): FlowNode['data']['nodeType'] {
  if (node.type_of === 'read' && node.parent_id === null) {
    return 'trigger';
  }
  if (node.type_of === 'filter' || node.action === 'filter') {
    return 'filter';
  }
  if (node.selected_api?.includes('Code') || node.action?.includes('code')) {
    return 'code';
  }
  if (node.action === 'branch' || node.selected_api?.includes('Branching')) {
    return 'branch';
  }
  if (node.type_of === 'write' || node.type_of === 'read') {
    return 'action';
  }
  return 'unknown';
}

/**
 * Extracts app name from selected_api string
 * Example: "MailchimpCLIAPI@1.14.1" -> "Mailchimp"
 */
function extractAppName(selectedApi: string | null): string {
  if (!selectedApi) return 'Unknown';

  // Remove version and CLI/API suffixes
  const cleaned = selectedApi
    .replace(/CLIAPI.*/, '')
    .replace(/API.*/, '')
    .replace(/CLI$/, '');

  // Add spaces before capital letters
  return cleaned.replace(/([A-Z])/g, ' $1').trim() || 'Unknown';
}

/**
 * Determines node type for simple format steps (based on app name)
 */
function determineStepNodeType(step: { type?: string; app: string; event: string }): FlowNode['data']['nodeType'] {
  const appLower = step.app.toLowerCase();

  // Check app name for special types
  if (appLower === 'filter' || appLower.includes('filter')) {
    return 'filter';
  }
  if (appLower.includes('code') || appLower === 'code_by_zapier') {
    return 'code';
  }
  if (appLower === 'paths' || appLower.includes('branch')) {
    return 'branch';
  }

  // Check explicit type if provided
  if (step.type === 'action') {
    return 'action';
  }

  // Default to action for most cases
  return 'action';
}

/**
 * Generates a human-readable label for a node
 */
function generateNodeLabel(node: ZapierNode, nodeType: FlowNode['data']['nodeType']): string {
  // Use meta.stepTitle if available
  if (node.meta?.stepTitle) {
    return node.meta.stepTitle;
  }

  // Use title if available
  if (node.title) {
    return node.title;
  }

  // Generate from action and app
  const appName = extractAppName(node.selected_api);
  const action = node.action || 'Action';

  if (nodeType === 'trigger') {
    return `${appName} Trigger`;
  }

  if (nodeType === 'filter') {
    return 'Filter';
  }

  if (nodeType === 'code') {
    return 'Code';
  }

  if (nodeType === 'branch') {
    return 'Path';
  }

  return `${appName}: ${action}`;
}

/**
 * Builds a tree structure from parent_id relationships
 */
function buildNodeTree(nodes: Record<string, ZapierNode>): {
  rootId: string | null;
  childrenMap: Map<string, string[]>;
  orderedNodes: ZapierNode[];
} {
  const childrenMap = new Map<string, string[]>();
  let rootId: string | null = null;

  // Validate nodes object
  if (!nodes || typeof nodes !== 'object' || Object.keys(nodes).length === 0) {
    throw new EmptyWorkflowError();
  }

  // First pass: find root and build children map
  Object.entries(nodes).forEach(([id, node]) => {
    // Validate node structure
    if (!node || typeof node !== 'object') {
      throw new InvalidNodeStructureError(id, 'Node is not a valid object');
    }

    if (node.parent_id === null || node.parent_id === undefined) {
      if (rootId !== null) {
        // Multiple roots found - invalid structure
        logError(new InvalidNodeStructureError(id, 'Multiple root nodes detected'), 'buildNodeTree');
      }
      rootId = id;
    } else {
      const parentId = node.parent_id.toString();
      if (!childrenMap.has(parentId)) {
        childrenMap.set(parentId, []);
      }
      childrenMap.get(parentId)!.push(id);
    }
  });

  // Validate that we found a root
  if (rootId === null) {
    throw new InvalidNodeStructureError('unknown', 'No root node found (no node with parent_id=null)');
  }

  // Traverse tree in order with cycle detection
  const orderedNodes: ZapierNode[] = [];
  const visited = new Set<string>();
  const visiting = new Set<string>(); // Tracks nodes in current path

  function traverse(nodeId: string, path: string[] = []) {
    // Cycle detection
    if (visiting.has(nodeId)) {
      throw new CyclicDependencyError();
    }

    // Already visited
    if (visited.has(nodeId)) {
      return;
    }

    // Mark as visiting (in current path)
    visiting.add(nodeId);

    const node = nodes[nodeId];
    if (node) {
      orderedNodes.push(node);

      const children = childrenMap.get(nodeId) || [];
      children.forEach(childId => {
        traverse(childId, [...path, nodeId]);
      });
    }

    // Mark as visited and remove from visiting
    visiting.delete(nodeId);
    visited.add(nodeId);
  }

  try {
    traverse(rootId);
  } catch (error) {
    logError(error, 'buildNodeTree traversal');
    throw error;
  }

  return { rootId, childrenMap, orderedNodes };
}

/**
 * Parse a single workflow (nodes format)
 */
function parseNodesWorkflow(workflow: ZapierWorkflow): ParsedWorkflow {
  if (!workflow.nodes) {
    return {
      id: workflow.id?.toString() || 'unknown',
      title: workflow.title || workflow.name || 'Untitled Workflow',
      description: workflow.description,
      status: workflow.status,
      nodes: [],
      edges: [],
      rawData: workflow,
    };
  }

  const { orderedNodes } = buildNodeTree(workflow.nodes);
  const flowNodes: FlowNode[] = [];
  const flowEdges: FlowEdge[] = [];

  // First, create all nodes with temporary positions
  orderedNodes.forEach((node) => {
    const nodeType = determineNodeType(node);
    const label = generateNodeLabel(node, nodeType);

    flowNodes.push({
      id: node.id.toString(),
      type: 'zapNode',
      position: { x: 0, y: 0 }, // Temporary position
      data: {
        label,
        app: extractAppName(node.selected_api),
        event: node.action || 'unknown',
        nodeType,
        config: node.params || {},
        rawNode: node,
        stepTitle: node.meta?.stepTitle,
        isLocked: true, // Default locked for freemium
        zapId: node.id.toString(), // For GDPR export variable resolution
      },
    });

    // Create edge from parent
    if (node.parent_id !== null) {
      flowEdges.push({
        id: `e-${node.parent_id}-${node.id}`,
        source: node.parent_id.toString(),
        target: node.id.toString(),
        type: 'smoothstep',
        animated: nodeType === 'trigger',
      });
    }
  });

  // Apply dagre layout if there's branching, otherwise use simple vertical layout
  let layoutedNodes: FlowNode[];
  if (hasBranching(flowEdges)) {
    layoutedNodes = calculateLayout(flowNodes, flowEdges);
  } else {
    // Simple vertical layout for linear workflows
    const NODE_SPACING_Y = 120;
    layoutedNodes = flowNodes.map((node, index) => ({
      ...node,
      position: { x: 250, y: index * NODE_SPACING_Y },
    }));
  }

  // Enhance edges with branch labels if applicable
  const enhancedEdges = enhanceEdgesForBranching(flowEdges, layoutedNodes);

  return {
    id: workflow.id?.toString() || 'unknown',
    title: workflow.title || workflow.name || 'Untitled Workflow',
    description: workflow.description,
    status: workflow.status,
    nodes: layoutedNodes,
    edges: enhancedEdges,
    rawData: workflow,
  };
}

/**
 * Parse a single workflow (trigger + steps format)
 */
function parseStepsWorkflow(workflow: ZapierWorkflow): ParsedWorkflow {
  const flowNodes: FlowNode[] = [];
  const flowEdges: FlowEdge[] = [];

  // Add trigger
  if (workflow.trigger) {
    flowNodes.push({
      id: 'trigger',
      type: 'zapNode',
      position: { x: 0, y: 0 }, // Temporary position
      data: {
        label: `${workflow.trigger.app} Trigger`,
        app: workflow.trigger.app,
        event: workflow.trigger.event,
        nodeType: 'trigger',
        config: workflow.trigger.config,
        rawNode: workflow.trigger,
        isLocked: true,
      },
    });
  }

  // Add steps
  if (workflow.steps) {
    workflow.steps.forEach((step, index) => {
      const stepId = `step-${index}`;
      const prevId = index === 0 ? 'trigger' : `step-${index - 1}`;
      const nodeType = determineStepNodeType(step);

      flowNodes.push({
        id: stepId,
        type: 'zapNode',
        position: { x: 0, y: 0 }, // Temporary position
        data: {
          label: `${step.app}: ${step.event}`,
          app: step.app,
          event: step.event,
          nodeType,
          config: step.config,
          rawNode: step,
          isLocked: true,
        },
      });

      flowEdges.push({
        id: `e-${prevId}-${stepId}`,
        source: prevId,
        target: stepId,
        type: 'smoothstep',
        animated: index === 0,
      });
    });
  }

  // Apply dagre layout if there's branching, otherwise use simple vertical layout
  let layoutedNodes: FlowNode[];
  if (hasBranching(flowEdges)) {
    layoutedNodes = calculateLayout(flowNodes, flowEdges);
  } else {
    // Simple vertical layout for linear workflows
    const NODE_SPACING_Y = 120;
    layoutedNodes = flowNodes.map((node, index) => ({
      ...node,
      position: { x: 250, y: index * NODE_SPACING_Y },
    }));
  }

  // Enhance edges with branch labels if applicable
  const enhancedEdges = enhanceEdgesForBranching(flowEdges, layoutedNodes);

  return {
    id: workflow.id?.toString() || workflow.name || 'unknown',
    title: workflow.title || workflow.name || 'Untitled Workflow',
    description: workflow.description,
    status: workflow.status,
    nodes: layoutedNodes,
    edges: enhancedEdges,
    rawData: workflow,
  };
}

/**
 * Main parser function
 * Handles multiple Zapier export formats:
 * 1. GDPR export with zaps array
 * 2. Single workflow with nodes
 * 3. Single workflow with trigger + steps
 */
export function parseZapierExport(json: ZapierExport): ParsedWorkflow[] {
  try {
    // Validate input
    if (!json || typeof json !== 'object') {
      throw new SchemaError(
        'Invalid JSON object',
        'The file does not contain valid JSON data. Please ensure you uploaded a Zapier export file.',
        true
      );
    }

    // Check for unsupported schema version
    if (json.metadata && json.metadata.version) {
      const version = json.metadata.version;
      // Currently only support gdpr_v1
      if (version !== 'gdpr_v1') {
        logError(
          new UnsupportedSchemaVersionError(version),
          'parseZapierExport - unsupported version detected'
        );
        // Don't throw - try to parse anyway
      }
    }

    const workflows: ParsedWorkflow[] = [];

    // Format 1: GDPR export with zaps array
    if (json.zaps && Array.isArray(json.zaps)) {
      if (json.zaps.length === 0) {
        throw new NoWorkflowsFoundError();
      }

      json.zaps.forEach((workflow, index) => {
        try {
          if (workflow.nodes) {
            workflows.push(parseNodesWorkflow(workflow));
          } else if (workflow.trigger || workflow.steps) {
            workflows.push(parseStepsWorkflow(workflow));
          } else {
            // Workflow has neither nodes nor trigger/steps
            logError(
              new EmptyWorkflowError(),
              `parseZapierExport - workflow ${index} is empty`
            );
          }
        } catch (error) {
          // Log error but continue processing other workflows
          logError(error, `parseZapierExport - failed to parse workflow ${index}`);

          // If it's a critical error, re-throw
          if (error instanceof CyclicDependencyError || error instanceof InvalidNodeStructureError) {
            throw error;
          }
        }
      });

      if (workflows.length === 0) {
        throw new NoWorkflowsFoundError();
      }

      return workflows;
    }

    // Format 2: Single workflow with nodes
    if (json.nodes) {
      try {
        workflows.push(parseNodesWorkflow(json as ZapierWorkflow));
        return workflows;
      } catch (error) {
        logError(error, 'parseZapierExport - failed to parse single workflow (nodes format)');
        throw error;
      }
    }

    // Format 3: Single workflow with trigger + steps
    if (json.trigger || json.steps) {
      try {
        workflows.push(parseStepsWorkflow(json as ZapierWorkflow));
        return workflows;
      } catch (error) {
        logError(error, 'parseZapierExport - failed to parse single workflow (steps format)');
        throw error;
      }
    }

    // No valid format detected
    if (workflows.length === 0) {
      throw new NoWorkflowsFoundError();
    }

    return workflows;
  } catch (error) {
    // Log the error
    logError(error, 'parseZapierExport');

    // Re-throw for caller to handle
    throw error;
  }
}

/**
 * Validates if a JSON object is a valid Zapier export
 */
export function validateZapierExport(json: any): {
  isValid: boolean;
  error?: string;
} {
  if (!json || typeof json !== 'object') {
    return { isValid: false, error: 'Invalid JSON format' };
  }

  // Check for any valid format
  const hasZaps = json.zaps && Array.isArray(json.zaps);
  const hasNodes = json.nodes && typeof json.nodes === 'object';
  const hasTriggerOrSteps = json.trigger || json.steps;

  if (!hasZaps && !hasNodes && !hasTriggerOrSteps) {
    return {
      isValid: false,
      error: 'File does not appear to be a valid Zapier export. Expected "zaps", "nodes", "trigger", or "steps" fields.',
    };
  }

  return { isValid: true };
}

/**
 * Type guard for checking if an object is a ZapierExport
 */
export function isZapierExport(obj: any): obj is ZapierExport {
  return validateZapierExport(obj).isValid;
}
